var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { loginApi } from "apis/eduidLogin";
import { navigatorCredentialsApi } from "apis/navigatorCredentials";
import { EduIDButton } from "components/Common/EduIDButton";
import { TextInput } from "components/Common/EduIDTextInput";
import { PassKey } from "components/Common/Passkey";
import { PasswordInput } from "components/Common/PasswordInput";
import { UserNameInput } from "components/Common/UserNameInput";
import { useAppDispatch, useAppSelector } from "eduid-hooks";
import { RESET_PASSWORD_PATH } from "helperFunctions/paths";
import { emailPattern } from "helperFunctions/validation/regexPatterns";
import { useCallback, useEffect, useRef, useState } from "react";
import { Field as FinalField, Form as FinalForm, useField } from "react-final-form";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate } from "react-router";
import loginSlice from "slices/Login";
import { clearNotifications } from "slices/Notifications";
import resetPasswordSlice from "slices/ResetPassword";
import passwordIcon from "../../../img/password-icon.svg";
import qrCode from "../../../img/qr-code.svg";
import { LoginAbortButton } from "./LoginAbortButton";
import { LoginAtServiceInfo } from "./LoginAtServiceInfo";
import { forgetThisDevice, RememberMeCheckbox } from "./NewDevice";
import { securityZoneAction, SecurityZoneInfo } from "./SecurityZoneInfo";
export function UsernamePw() {
    const dispatch = useAppDispatch();
    const ref = useAppSelector((state) => state.login.ref);
    const service_info = useAppSelector((state) => state.login.service_info);
    const webauthn = useAppSelector((state) => state.login.authn_options.webauthn);
    const [fetchUsernamePassword] = loginApi.useLazyFetchUsernamePasswordQuery();
    const [fetchMfaAuth] = loginApi.useLazyFetchMfaAuthQuery();
    const [performAuthentication] = navigatorCredentialsApi.useLazyPerformAuthenticationQuery();
    const [conditionalAuthTrigger, setConditionalAuthTrigger] = useState(0);
    const conditionalQueryRef = useRef(null);
    // Cached challenge promise: both the conditional auth effect and the passkey button
    // share a single fetchMfaAuth call to avoid concurrent backend requests that race
    // on session.mfa_action and cause SessionOutOfSync.
    const challengePromiseRef = useRef(null);
    let loginHeading;
    if (securityZoneAction) {
        loginHeading = (_jsx(FormattedMessage, { id: "usernamePw.heading", defaultMessage: "Re-authentication: with Password", description: "Security zone username and Password heading" }));
    }
    else if (webauthn) {
        loginHeading = (_jsx(FormattedMessage, { id: "usernamePw.front", defaultMessage: "Log in: with Password or Passkey", description: "Login front page with passkey" }));
    }
    else {
        loginHeading = (_jsx(FormattedMessage, { id: "usernamePw.frontPage", defaultMessage: "Log in: with Password", description: "Login front page" }));
    }
    function handleSubmitUsernamePw(values) {
        return __awaiter(this, void 0, void 0, function* () {
            const errors = {};
            if (ref && values.username && values.currentPassword) {
                /* Send username and password to backend for authentication. If the response is successful,
                 * trigger a call to the /next endpoint to get the next step in the login process.
                 */
                const response = yield fetchUsernamePassword({
                    ref,
                    username: values.username,
                    password: values.currentPassword,
                });
                if (response.isSuccess) {
                    dispatch(clearNotifications());
                    if (response.data.payload.finished) {
                        dispatch(loginSlice.actions.callLoginNext());
                    }
                }
                return;
            }
            if (!values.username) {
                errors.username = "required";
            }
            if (!values.currentPassword) {
                errors.currentPassword = "required";
            }
            return errors;
        });
    }
    // Fetch a challenge once and cache the promise. Both the conditional auth effect
    // and the passkey button reuse the same promise, so only one POST /mfa_auth is
    // in-flight at a time for challenge creation.
    const fetchChallengeOnce = useCallback(() => {
        if (!challengePromiseRef.current) {
            if (!ref)
                return Promise.resolve(undefined);
            challengePromiseRef.current = fetchMfaAuth({ ref })
                .then((response) => {
                if (response.isSuccess) {
                    return response.data.payload.webauthn_options;
                }
                return undefined;
            })
                .catch(() => {
                // Clear the cache on failure so the next attempt can retry
                challengePromiseRef.current = null;
                return undefined;
            });
        }
        return challengePromiseRef.current;
    }, [fetchMfaAuth, ref]);
    function getChallenge() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            // Abort any in-flight conditional auth before starting staged auth,
            // since the browser only allows one pending navigator.credentials.get() at a time.
            (_a = conditionalQueryRef.current) === null || _a === void 0 ? void 0 : _a.abort();
            return fetchChallengeOnce();
        });
    }
    // Track the last credential submission so that restartConditionalAuth
    // (and the effect) can wait for it before fetching a new challenge.
    const credentialSubmissionRef = useRef(null);
    function useCredential(credential) {
        return __awaiter(this, void 0, void 0, function* () {
            if (ref) {
                const promise = fetchMfaAuth({ ref: ref, webauthn_response: credential });
                credentialSubmissionRef.current = promise;
                yield promise;
            }
        });
    }
    function restartConditionalAuth() {
        setConditionalAuthTrigger((n) => n + 1);
    }
    useEffect(() => {
        if (!webauthn) {
            return;
        }
        let cancelled = false;
        // Clear cached challenge so a fresh one is fetched on each trigger
        challengePromiseRef.current = null;
        const conditionalAuthentication = () => __awaiter(this, void 0, void 0, function* () {
            if (!ref) {
                return;
            }
            // Wait for any in-flight credential submission to finish before fetching
            // a new challenge, otherwise the new challenge fetch and the old submission
            // will race on session.mfa_action in the backend.
            if (credentialSubmissionRef.current) {
                yield credentialSubmissionRef.current.catch(() => { });
                credentialSubmissionRef.current = null;
            }
            if (cancelled) {
                return;
            }
            const webauthn_options = yield fetchChallengeOnce();
            // After the await, check if this effect invocation was already cleaned up
            // (e.g. React StrictMode unmount-remount cycle). If so, bail out.
            if (cancelled) {
                return;
            }
            if (webauthn_options) {
                const queryPromise = performAuthentication({
                    webauth_options: webauthn_options,
                    mediation: "conditional",
                });
                conditionalQueryRef.current = queryPromise;
                const result = yield queryPromise;
                if (cancelled) {
                    return;
                }
                if (result.isSuccess) {
                    const promise = fetchMfaAuth({ ref: ref, webauthn_response: result.data });
                    credentialSubmissionRef.current = promise;
                    yield promise;
                }
            }
        });
        conditionalAuthentication();
        return () => {
            var _a;
            cancelled = true;
            (_a = conditionalQueryRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        };
    }, [fetchChallengeOnce, fetchMfaAuth, performAuthentication, ref, webauthn, conditionalAuthTrigger]);
    return (_jsxs(_Fragment, { children: [_jsxs("section", { className: "intro", children: [_jsx("h1", { children: loginHeading }), _jsx("div", { className: "lead", children: _jsx(LoginAtServiceInfo, { service_info: service_info }) }), _jsx(SecurityZoneInfo, {})] }), _jsxs("section", { className: "username-pw-option", children: [_jsx(FinalForm, { "aria-label": "login form", onSubmit: handleSubmitUsernamePw, render: (formProps) => {
                            return (_jsxs("form", { onSubmit: formProps.handleSubmit, children: [_jsx(UsernameInputPart, {}), _jsx(PasswordInput, { name: "currentPassword", autoComplete: "current-password" }), !securityZoneAction && _jsx(RenderResetPasswordLink, {}), _jsxs("div", { className: "buttons", children: [!securityZoneAction && _jsx(LoginAbortButton, {}), _jsx(UsernamePwSubmitButton, Object.assign({}, formProps)), _jsx(UsernamePwAnotherDeviceButton, {})] })] }));
                        } }), webauthn && (_jsxs("div", { className: "or-container", children: [_jsx("div", { className: "line" }), _jsx("span", { children: _jsx(FormattedMessage, { id: "usernamePw.alternative", defaultMessage: "or log in with passkey?", description: "Alternative login passkey option" }) }), _jsx("div", { className: "line" })] }))] }), webauthn && (_jsxs(_Fragment, { children: [_jsx("section", { className: "passkey-option", children: _jsx(PassKey, { setup: getChallenge, onSuccess: useCredential, onComplete: restartConditionalAuth, discoverable: webauthn }) }), _jsx("hr", { className: "border-line" })] })), _jsx(RememberMeCheckbox, {})] }));
}
function UsernameInputPart() {
    const authn_options = useAppSelector((state) => state.login.authn_options);
    const webauthn = useAppSelector((state) => state.login.authn_options.webauthn);
    const dispatch = useAppDispatch();
    function handleClickWrongPerson() {
        forgetThisDevice(dispatch);
        // re-fetch '/next' now that the conditions for logging in has changed
        dispatch(loginSlice.actions.callLoginNext());
    }
    if (authn_options.forced_username) {
        return (_jsxs(_Fragment, { children: [!securityZoneAction && (_jsxs("div", { className: "welcome-back-container", children: [_jsx("legend", { children: _jsx(FormattedMessage, { id: "usernamePw.welcome", defaultMessage: "Welcome back, {username}!", description: "Login username input", values: {
                                    username: _jsx("strong", { children: authn_options.display_name }),
                                } }) }), _jsx(EduIDButton, { buttonstyle: "link normal-case", id: "wrong-person-button", onClick: handleClickWrongPerson, children: _jsx(FormattedMessage, { id: "usernamePw.notYou", defaultMessage: "Different user?", description: "Login username input" }) })] })), _jsx(FinalField, { required: true, disabled: true, component: TextInput, componentClass: "input", name: "username", autoComplete: "username", defaultValue: authn_options.forced_username, label: _jsx(FormattedMessage, { id: "common.username", defaultMessage: "Username", description: "username input field label" }) })] }));
    }
    return (_jsx(UserNameInput, { name: "username", autoFocus: true, required: true, autoComplete: webauthn ? "username webauthn" : "username" }));
}
function RenderResetPasswordLink() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const usernameField = useField("username");
    const isValidEmail = emailPattern.test(usernameField.input.value);
    const sendLink = (e) => {
        e.preventDefault();
        /* Propagate the email address to the reset password slice if valid, or if empty. */
        if ((isValidEmail && usernameField.meta.valid) || !usernameField.input.value) {
            dispatch(resetPasswordSlice.actions.setEmailAddress(usernameField.input.value));
        }
        dispatch(clearNotifications());
        navigate(RESET_PASSWORD_PATH);
    };
    return (_jsx(Link, { id: "link-forgot-password", className: "text-small", to: "/", onClick: sendLink, children: _jsx(FormattedMessage, { id: "usernamePw.link", defaultMessage: "Forgot your password?", description: "Reset password link" }) }));
}
export function UsernamePwSubmitButton({ values, handleSubmit, submitError, dirtySinceLastSubmit, hasValidationErrors, }) {
    /* Disable the button when:
     *   - there is a form validation error
     *   - the last submit resulted in a submitError, and no changes have been made since
     */
    const _hasUserNameValue = Boolean(values === null || values === void 0 ? void 0 : values["username"]);
    const _hasPasswordValue = Boolean(values === null || values === void 0 ? void 0 : values["currentPassword"]);
    const _inputValues = securityZoneAction
        ? Boolean(_hasPasswordValue)
        : Boolean(_hasUserNameValue && _hasPasswordValue);
    const _submitError = Boolean(submitError && !dirtySinceLastSubmit);
    const hasErrors = hasValidationErrors !== null && hasValidationErrors !== void 0 ? hasValidationErrors : true;
    const hasSubmitError = _submitError !== null && _submitError !== void 0 ? _submitError : true;
    const _disabled = Boolean(hasErrors || !_inputValues || hasSubmitError);
    return (_jsxs(EduIDButton, { buttonstyle: "primary icon", type: "submit", "aria-disabled": _disabled, id: "login-form-button", onClick: handleSubmit, children: [_jsx("img", { className: "password-icon", height: "20", alt: "password icon", src: passwordIcon }), _jsx(FormattedMessage, { id: "common.logIn", defaultMessage: "log in", description: "Login front page pw" })] }));
}
function UsernamePwAnotherDeviceButton() {
    const options = useAppSelector((state) => state.login.authn_options);
    const dispatch = useAppDispatch();
    const usernameField = useField("username");
    if (!options.other_device || securityZoneAction) {
        return null;
    }
    function handleOnClick(e) {
        return __awaiter(this, void 0, void 0, function* () {
            e.preventDefault(); // don't submit the form when this button is clicked
            dispatch(loginSlice.actions.startLoginWithAnotherDevice({ username: usernameField.input.value || undefined }));
        });
    }
    return (_jsxs(EduIDButton, { buttonstyle: "primary icon", onClick: handleOnClick, id: "login-other-device-button", children: [_jsx("img", { className: "qr-icon", height: "20", alt: "qr icon", src: qrCode }), _jsx(FormattedMessage, { id: "usernamePw.other", defaultMessage: "other device", description: "Login UsernamePw other device" })] }));
}
